import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 2500;
const MAX_TRIES = 20;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState(null);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setPlan(data.plan);
        if (data.payment_status === "paid") {
          setState("paid");
          refresh?.();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {
        setState("error");
        return;
      }
      if (++tries >= MAX_TRIES) { setState("timeout"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => clearTimeout(timer);
    // `refresh` from context is recreated per render; polling must not restart on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const copy = {
    checking: { icon: Clock, title: "Confirming your payment…",
      sub: "This usually takes a few seconds. Please keep this page open." },
    paid: { icon: CheckCircle2, title: "Subscription active",
      sub: `Your ${plan ? plan.toUpperCase() : ""} plan is live. Full RFQ access has been unlocked for your company.` },
    failed: { icon: XCircle, title: "Payment not completed",
      sub: "No charge was made. You can try again from the subscription page." },
    timeout: { icon: Clock, title: "Still confirming",
      sub: "Your payment is taking longer than usual to confirm. Reload this page in a minute — nothing is lost." },
    error: { icon: XCircle, title: "We could not find that payment",
      sub: "Please return to the subscription page and start again." },
  }[state];
  const Icon = copy.icon;

  return (
    <section className="mx-auto max-w-[640px] px-5 py-20" data-testid="payment-success-page">
      <div className="rounded-[18px] border border-[#DFE6ED] bg-white p-10 shadow-[0_18px_50px_-30px_rgba(16,42,67,0.4)]">
        <Icon className={`h-10 w-10 ${state === "paid" ? "text-[#147D78]" : "text-[#102A43]"}`} />
        <h1 className="mt-5 font-display text-3xl font-black tracking-tight text-[#102A43]"
          data-testid="payment-status-title">{copy.title}</h1>
        <p className="mt-3 text-base text-[#667788]" data-testid="payment-status-sub">{copy.sub}</p>
        <p className="mt-6 text-xs text-[#8598ac]" data-testid="payment-session-id">
          Reference: {sessionId || "—"}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/supplier/subscription" data-testid="back-to-subscription-btn"
            className="rounded-full bg-[#102A43] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0b1f33]">
            Back to subscription
          </Link>
          <Link to="/supplier/rfqs" data-testid="go-to-rfqs-btn"
            className="rounded-full border border-[#DFE6ED] px-6 py-3 text-sm font-bold text-[#102A43] transition-colors hover:border-[#102A43]">
            View matching enquiries
          </Link>
        </div>
      </div>
    </section>
  );
}

export function PaymentCancel() {
  return (
    <section className="mx-auto max-w-[640px] px-5 py-20" data-testid="payment-cancel-page">
      <div className="rounded-[18px] border border-[#DFE6ED] bg-white p-10">
        <XCircle className="h-10 w-10 text-[#102A43]" />
        <h1 className="mt-5 font-display text-3xl font-black tracking-tight text-[#102A43]">
          Checkout cancelled
        </h1>
        <p className="mt-3 text-base text-[#667788]">
          No payment was taken. Your current plan is unchanged.
        </p>
        <Link to="/supplier/subscription" data-testid="cancel-back-btn"
          className="mt-8 inline-block rounded-full bg-[#102A43] px-6 py-3 text-sm font-bold text-white">
          Back to subscription
        </Link>
      </div>
    </section>
  );
}
